import type { Model } from '../types';
import { api } from './apiClient';

export interface ChatMessageDTO {
  role: 'user' | 'agent' | 'assistant';
  content: string;
}

export interface ChatRequest {
  model: string;
  systemPrompt?: string;
  messages: ChatMessageDTO[];
  temperature?: number;
  maxTokens?: number;
  // RAG (opcional): habilita retrieval dos arquivos do agente e/ou da base vinculada.
  agentId?: string;
  embeddingModel?: string;
  knowledgeBaseId?: string;
}

export interface ChatSource {
  fileName: string;
  score: number;
  scope: 'agent' | 'kb';
  kb?: string;
}

export interface ChatCost {
  costUsd: number;
  monthlySpentUsd: number;
  monthlyCapUsd: number;
  overPerRun: boolean;
}

export interface ChatResponse {
  reply: string;
  finishReason?: string | null;
  usage?: unknown;
  sources?: ChatSource[];
  cost?: ChatCost;
}

export const modelsService = {
  // Catálogo de modelos de chat (para admin/labels/seletores).
  async list(): Promise<Model[]> {
    const res = await api.get<{ models: Model[] }>('/models?kind=chat');
    return res.models;
  },

  // Modelos de embedding habilitados (seletor de RAG).
  async listEmbeddings(): Promise<Model[]> {
    const res = await api.get<{ models: Model[] }>('/models/embeddings');
    return res.models;
  },

  // Modelos efetivos que o usuário logado pode usar (Pessoa ∩ Área).
  async listAllowed(): Promise<Model[]> {
    const res = await api.get<{ models: Model[] }>('/models/allowed');
    return res.models;
  },

  // --- Governança (admin) ---
  async getAreaModels(areaId: string): Promise<string[]> {
    const res = await api.get<{ modelIds: string[] }>(`/budget/areas/${encodeURIComponent(areaId)}/models`);
    return res.modelIds;
  },
  async setAreaModels(areaId: string, modelIds: string[]): Promise<string[]> {
    const res = await api.put<{ modelIds: string[] }>(`/budget/areas/${encodeURIComponent(areaId)}/models`, { modelIds });
    return res.modelIds;
  },
  async getPersonModels(personId: string): Promise<string[]> {
    const res = await api.get<{ modelIds: string[] }>(`/budget/people/${encodeURIComponent(personId)}/models`);
    return res.modelIds;
  },
  async setPersonModels(personId: string, modelIds: string[]): Promise<string[]> {
    const res = await api.put<{ modelIds: string[] }>(`/budget/people/${encodeURIComponent(personId)}/models`, { modelIds });
    return res.modelIds;
  },

  // Chat real (proxy para o Azure no backend).
  async chat(req: ChatRequest): Promise<ChatResponse> {
    return api.post<ChatResponse>('/agents/chat', req);
  },
};
